import React, { useEffect, useRef, useState } from 'react';
import { Camera, X, Loader2, Keyboard, ScanLine } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import { driverApi } from '../services/api';
import { toast } from 'sonner';

/**
 * QrPickupScanner — opens the device camera (html5-qrcode) and scans the
 * customer/vendor QR. On a successful match it calls the supplied verify
 * function (defaults to the driver endpoint) and triggers onSuccess.
 *
 * Props:
 *   - orderId: id of the order to verify
 *   - open / onClose: dialog visibility
 *   - onSuccess: called on successful verify
 *   - verifyFn(orderId, token) -> Promise — overrides the default driver verify.
 *     Used by vendors to call /vendor-admin/orders/{id}/confirm-pickup instead.
 *   - title / helpText: i18n overrides
 */
const QrPickupScanner = ({ orderId, open, onClose, onSuccess, verifyFn, title, helpText }) => {
  const { t } = useTranslation();
  const scannerRef = useRef(null);
  const [mode, setMode] = useState('camera'); // camera | manual
  const [manualToken, setManualToken] = useState('');
  const [verifying, setVerifying] = useState(false);

  useEffect(() => {
    if (!open || mode !== 'camera') return;
    let scanner;
    (async () => {
      try {
        const { Html5Qrcode } = await import('html5-qrcode');
        scanner = new Html5Qrcode('swiftdash-qr-region');
        scannerRef.current = scanner;
        const config = { fps: 10, qrbox: { width: 240, height: 240 } };
        await scanner.start({ facingMode: 'environment' }, config,
          async (decoded) => { await verify(decoded); },
          () => {}, // ignore scan errors
        );
      } catch (e) {
        toast.error('Could not start camera — try manual entry.');
        setMode('manual');
      }
    })();
    return () => {
      try {
        if (scannerRef.current?.isScanning) scannerRef.current.stop().catch(() => {});
      } catch (e) { /* */ }
    };
    // eslint-disable-next-line
  }, [open, mode]);

  const verify = async (token) => {
    if (verifying) return;
    setVerifying(true);
    try {
      try { await scannerRef.current?.stop(); } catch (e) { /* */ }
      if (verifyFn) {
        await verifyFn(orderId, token);
      } else {
        await driverApi.verifyQr(orderId, token);
      }
      toast.success(t('driver.qr_valid', 'QR verified — order picked up!'));
      onSuccess?.();
      onClose?.();
    } catch (err) {
      const msg = err?.response?.data?.detail || t('driver.qr_mismatch', "This QR doesn't match the order you're picking up.");
      toast.error(msg);
      // Restart scanning
      if (mode === 'camera' && scannerRef.current && !scannerRef.current.isScanning) {
        try {
          await scannerRef.current.start({ facingMode: 'environment' }, { fps: 10, qrbox: { width: 240, height: 240 } },
            async (decoded) => { await verify(decoded); }, () => {});
        } catch (e) { /* */ }
      }
    } finally { setVerifying(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-md p-0 overflow-hidden">
        <DialogHeader className="px-5 pt-5">
          <DialogTitle className="flex items-center gap-2">
            <ScanLine className="h-5 w-5 text-emerald-600" />
            {title || t('driver.scan_to_pickup', 'Scan to pick up')}
          </DialogTitle>
        </DialogHeader>
        <p className="px-5 text-sm text-slate-500 mt-1">{helpText || t('driver.scan_qr_help', "Point your camera at the QR code shown by the store.")}</p>

        <div className="px-5 mt-3">
          <div className="flex gap-2 text-xs">
            <button onClick={() => setMode('camera')}
              className={`px-3 py-1 rounded-full ${mode === 'camera' ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-700'}`} data-testid="qr-mode-camera">
              <Camera className="h-3 w-3 inline mr-1" /> Camera
            </button>
            <button onClick={() => setMode('manual')}
              className={`px-3 py-1 rounded-full ${mode === 'manual' ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-700'}`} data-testid="qr-mode-manual">
              <Keyboard className="h-3 w-3 inline mr-1" /> Manual
            </button>
          </div>
        </div>

        {mode === 'camera' ? (
          <div className="relative mt-3 bg-black aspect-square">
            <div id="swiftdash-qr-region" className="w-full h-full" data-testid="qr-camera-region" />
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
              <div className="w-56 h-56 border-2 border-emerald-400/70 rounded-2xl shadow-[0_0_40px_rgba(16,185,129,0.4)]" />
            </div>
          </div>
        ) : (
          <div className="p-5 pb-3 space-y-2">
            <label className="text-xs text-slate-600">Paste QR token or type the 6-digit backup code</label>
            <Input value={manualToken} onChange={(e) => setManualToken(e.target.value)}
              placeholder="e.g. 123456" autoFocus data-testid="qr-manual-input" />
            <p className="text-[11px] text-slate-400">Camera not working? Ask the customer for the backup code shown under their QR.</p>
            <Button onClick={() => verify(manualToken)} disabled={!manualToken || verifying}
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="qr-manual-submit">
              {verifying ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <ScanLine className="h-4 w-4 mr-1.5" />}
              Verify
            </Button>
          </div>
        )}

        <button onClick={onClose} className="absolute top-3 right-3 p-1.5 bg-white/90 rounded-full" aria-label="close" data-testid="qr-close-btn">
          <X className="h-4 w-4" />
        </button>
      </DialogContent>
    </Dialog>
  );
};

export default QrPickupScanner;
